/**
 * Client-side auth: API calls to /api/auth/* and JWT storage in
 * sessionStorage. The token is short-lived (24h server-side) and scoped to
 * the tab — closing the tab logs the player out, which is the right default
 * for an MVP browser game.
 */
import type { IJwtPayload } from '@pokemiu/shared';

const STORAGE_KEY = 'pokemiu.jwt';

const API_BASE: string =
  (import.meta.env.VITE_SOCKET_URL as string | undefined) ?? 'http://localhost:3000';

export interface Credentials {
  username: string;
  password: string;
}

export class AuthError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

interface AuthSuccess {
  token: string;
}

interface AuthErrorBody {
  error?: string;
}

async function postAuth(path: '/register' | '/login', creds: Credentials): Promise<string> {
  const res = await fetch(`${API_BASE}/api/auth${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(creds),
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // ignore — non-JSON response is itself a failure case
  }
  if (!res.ok) {
    const code = (body as AuthErrorBody | null)?.error ?? `http_${res.status}`;
    throw new AuthError(code, humanMessage(code));
  }
  const token = (body as AuthSuccess | null)?.token;
  if (typeof token !== 'string' || token.length === 0) {
    throw new AuthError('malformed_response', 'Server returned no token');
  }
  return token;
}

function humanMessage(code: string): string {
  switch (code) {
    case 'invalid_input':
      return 'Username and password are required (password ≥ 8 characters).';
    case 'username_taken':
      return 'That username is already in use.';
    case 'invalid_credentials':
      return 'Invalid username or password.';
    case 'http_500':
      return 'Server error — please try again.';
    default:
      return code;
  }
}

export async function register(creds: Credentials): Promise<string> {
  return postAuth('/register', creds);
}

export async function login(creds: Credentials): Promise<string> {
  return postAuth('/login', creds);
}

export function getStoredToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function setStoredToken(token: string): void {
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function clearStoredToken(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Decode the JWT body without verifying — useful for showing the username on
 * the welcome banner. The server is the only authority on validity; this is
 * cosmetic only.
 */
export function readPayload(token: string): IJwtPayload | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as IJwtPayload;
  } catch {
    return null;
  }
}
